import {
  isSubPortfolioLifeGroupRef,
  isSubPortfolioLifeRef,
} from '@festina/unitlink-general-types/unitlink'
import { useCallback } from 'react'

import type { PortfolioKeys } from '../../context/VersionProvider'
import type { useLazyRenderPolicy } from '../../hooks/useLazyRenderPolicy'
import { useTranslateCover } from '../../hooks/useTranslateCover'
import { useHook } from '../../lenteHooks'

type Props = NonNullable<Parameters<typeof useLazyRenderPolicy>[0]>

export const useRenderPortfolioKeys = (props: Props = {}) => {
  const translateCover = useTranslateCover()
  const renderPolicy = useHook('pages.commitment.useLazyRenderPolicy')(props)

  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { data: portfolioVersion } = useHook(
    'fetchers.unitlink.useFetchPortfolioVersionLatest',
  )({
    tenantId,
    ownerId,
  })

  return useCallback(
    ({ subPortfolioGroupKey, subPortfolioKey }: PortfolioKeys): string => {
      if (subPortfolioKey) {
        const subPortfolioVersion = portfolioVersion?.subPortfolioGroupVersions
          ?.flatMap((subPortfolioGroupVersion) =>
            !subPortfolioGroupKey ||
            subPortfolioGroupVersion?.subPortfolioGroupKey ===
              subPortfolioGroupKey
              ? subPortfolioGroupVersion?.subPortfolioVersions
              : undefined,
          )
          .find(
            (subPortfolioVersion) =>
              subPortfolioVersion?.subPortfolioKey === subPortfolioKey,
          )

        if (!isSubPortfolioLifeRef(subPortfolioVersion?.subPortfolioRef)) {
          return subPortfolioKey
        }

        const { policyKey, coverKey } = subPortfolioVersion.subPortfolioRef
        const policy = renderPolicy(policyKey)
        const cover = translateCover(coverKey)
        if (policyKey && coverKey) {
          return `${policy} - ${cover}`
        } else if (policyKey) {
          return policy
        } else if (coverKey) {
          return cover
        } else {
          return subPortfolioKey
        }
      } else if (subPortfolioGroupKey) {
        // This else-if branch is currently unused, but would otherwise need to be aligned with SubCommitmentName.
        const subPortfolioGroupVersion =
          portfolioVersion?.subPortfolioGroupVersions?.find(
            (subPortfolioGroupVersion) =>
              subPortfolioGroupVersion?.subPortfolioGroupKey ===
              subPortfolioGroupKey,
          )

        return isSubPortfolioLifeGroupRef(
          subPortfolioGroupVersion?.subPortfolioGroupRef,
        ) && subPortfolioGroupVersion?.subPortfolioGroupRef.policyKey
          ? renderPolicy(
              subPortfolioGroupVersion.subPortfolioGroupRef.policyKey,
            )
          : subPortfolioGroupKey
      } else {
        return ''
      }
    },
    [portfolioVersion?.subPortfolioGroupVersions, renderPolicy, translateCover],
  )
}
